import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import {
  CommonMediaQueries,
  IconModule,
  MediaScreenService,
} from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';

import { InfiniteScrollDirective, ScrollInfo } from '@iw/core/directives';
import { BaseFilters, Column, GetPaginationRes } from '@iw/core/entities';

import { ShimmerComponent } from '../shimmer/shimmer.component';
import { SortComponent, SortDirective } from '../sort';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

@Component({
  selector: 'iw-table',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    NgTemplateOutlet,
    ShimmerComponent,
    SortComponent,
    SortDirective,
    IconModule,
    InfiniteScrollDirective,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.grid-template-columns]': 'gridTemplateColumns()',
  },
})
export class TableComponent implements OnInit {
  readonly #mediaScreenService = inject(MediaScreenService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslateService);

  readonly columns = input<Column[]>();
  readonly data = input<GetPaginationRes<any>>();
  readonly filters = input<BaseFilters>(null);
  readonly gridTemplateColumns = input<string>('');
  readonly isLoading = input<boolean>(false);
  readonly emptyTitle = input<string>();
  readonly emptyMessage = input<string>();
  readonly divideRows = input<boolean>(true);

  readonly sortEvent = output<string>();
  readonly tableScrolled = output<ScrollInfo>();
  readonly loadMore = output<void>();

  readonly tableBody = viewChild<ElementRef<HTMLDivElement>>('tableBody');
  readonly templateMobile = contentChild<TemplateRef<unknown>>('mobile');
  readonly templateEmpty = contentChild<TemplateRef<unknown>>('empty');
  readonly templateRef = contentChild(TemplateRef);

  readonly filtersChangeEffect = effect(() => {
    const filters = this.filters();

    if (filters) {
      this.sortControl.setValue(filters.sort);
    }
  });

  readonly items = computed(() => {
    const items = this.data()?.items ?? [];
    let prevDay = '';

    return items.map((item) => {
      const currentDay = dayjs(item.createdAt).format('YYYY-MM-DD');

      if (currentDay !== prevDay) {
        item.header = this.#getDayTitle(item.createdAt);
      }

      prevDay = currentDay;
      return item;
    });
  });

  readonly isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  readonly sort = '';
  readonly sortControl = new FormControl<string>(null);

  ngOnInit() {
    this.sortControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value: string) => {
        this.sortEvent.emit(value);
      });
  }

  #getDayTitle = (date: Date): string => {
    const dd = dayjs(date);

    if (dd.isToday())
      return this.#translateService.instant('table.header.today');
    if (dd.isYesterday())
      return this.#translateService.instant('table.header.yesterday');
    return dd.format('DD.MM.YYYY');
  };
}
